'use client';

import { Box, Flex, freddyTheme, Link, Typography } from '@coolblue-development/freddy';
import cancelProductPlusConsentTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/cancelProductPlusConsentTrigger';
import closeOverlayTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/closeOverlayTrigger';
import openOverlayTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/openOverlayTrigger';
import showProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/showProductPlusTrigger';
import { Checkmark } from '@coolblue-development/icons';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import {
  InfoUSP,
  SelectableBox,
  SelectableBoxGroup
} from '@coolblue-development/next-patterns/client';
import type { PickParameterPropertyType } from '@hooks/PickParameterPropertyType';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import ConsentModal from '../ConsentModal';
import { getSubFeature } from '../helpers';

export type EntranceProps = {
  freeWashingUrl: string;
  freeDryingUrl: string;
  isFreeDrying: boolean;
  productImageId: string;
  productId: number;
};

type SubFeature = PickParameterPropertyType<typeof openOverlayTrigger, 'sub_feature'>;

export default function Entrance({
  freeWashingUrl,
  freeDryingUrl,
  isFreeDrying,
  productImageId,
  productId
}: EntranceProps) {
  const { t, Trans } = useTranslation('webshop.productPage.freeWashing');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  const sub_feature: SubFeature = getSubFeature(isFreeDrying);

  const openOverlayTriggerParams = {
    sub_feature,
    content: 'free_washing_proposition_content'
  };

  const closeOverlayTriggerParams = {
    sub_feature
  };

  const cancelProductPlusConsentTriggerParams = {
    sub_feature
  };

  const onCheckboxChange = () => {
    if (!isChecked) {
      setOpenModal(true);
      trackEvent(openOverlayTrigger(openOverlayTriggerParams));
    }

    if (isChecked) {
      trackEvent(cancelProductPlusConsentTrigger(cancelProductPlusConsentTriggerParams));
    }

    setIsChecked(false);
  };

  const onModalClose = () => {
    setOpenModal(false);
    trackEvent(closeOverlayTrigger(closeOverlayTriggerParams));
  };

  const dictionaryKeySuffix = isFreeDrying ? 'Drying' : '';

  return (
    <Box>
      <TrackImmediate trigger={showProductPlusTrigger({ sub_feature })} />
      <Box mb={200}>
        <InfoUSP
          productTitle=''
          promoValue=''
          customTitle={
            <Typography fontWeight={600}>
              <Trans
                components={{
                  strong: (
                    <Typography
                      component='span'
                      fontWeight={700}
                      color={freddyTheme.color.font.accented}
                    />
                  )
                }}
                i18nKey={`usp${dictionaryKeySuffix}`}
                t={t}
              />
            </Typography>
          }
          isProductTitleEmphasized
        />
      </Box>

      <SelectableBoxGroup
        name='free-washing'
        value={isChecked ? ['checked'] : []}
        type='checkbox'
        inputPlacement='left'
      >
        <SelectableBox
          value='checked'
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onCheckboxChange();
          }}
        >
          <Link component='span' fontWeight={700}>
            {t('title')}
          </Link>

          <Typography mt={100} variant='caption'>
            {t(`description${dictionaryKeySuffix}`)}
          </Typography>

          {isChecked && (
            <Flex alignItems='center' mt={100} gap={100}>
              <Checkmark color={freddyTheme.color.icon.sentimentPositiveContrast} />
              <Typography color='sentimentPositiveContrast'>{t('email')}</Typography>
            </Flex>
          )}
        </SelectableBox>
      </SelectableBoxGroup>

      {openModal && (
        <ConsentModal
          freeWashingUrl={freeWashingUrl}
          freeDryingUrl={freeDryingUrl}
          isFreeDrying={isFreeDrying}
          onClose={onModalClose}
          open={openModal}
          onChange={setIsChecked}
          checked={isChecked}
          productImageId={productImageId}
          productId={productId}
        />
      )}
    </Box>
  );
}
